// Random-but-stable category fallback images for BusinessCard.
//
// Images live in `client/src/assets/category-images/<slug>/`. Each subfolder
// maps to a slugified business category; the special `_default` folder is the
// fallback when no category-specific folder matches.
//
// See `client/src/assets/category-images/README.md` for the folder convention.

const DEFAULT_FOLDER = "_default";

// webpack-only API. Discovers every image file under category-images at build
// time. The relative key looks like "./restaurant/main-street.jpg" — we parse
// the first path segment as the slug.
const imageContext = require.context(
  "../assets/category-images",
  true,
  /\.(png|jpe?g|webp|gif)$/i
);

// Build a map of slug → array of resolved URLs once at module load.
const imagesBySlug = (() => {
  const out = {};
  imageContext.keys().forEach((key) => {
    const match = /^\.\/([^/]+)\//.exec(key);
    if (!match) return;
    const slug = match[1];
    const url = imageContext(key);
    // require.context can return either a string (file-loader) or
    // `{ default: string }` (CRA 5+). Normalize.
    const resolved = typeof url === "string" ? url : url?.default;
    if (!resolved) return;
    if (!out[slug]) out[slug] = [];
    out[slug].push(resolved);
  });
  // Stable per-slug ordering so the deterministic hash maps the same way
  // across reloads of the same build.
  Object.keys(out).forEach((slug) => out[slug].sort());
  return out;
})();

export const slugifyCategory = (raw) => {
  if (!raw) return "";
  const text = typeof raw === "object" ? raw.en || raw.fr || "" : String(raw);
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
};

// Tiny string hash (djb2 variant). Stable across browsers, doesn't need crypto.
const hashString = (str) => {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash + str.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
};

// Returns a URL for the given category + business, or null if no images are
// configured (in which case callers should fall back to a placeholder icon).
export const getCategoryImage = (category, businessId) => {
  if (!businessId) return null;
  const slug = slugifyCategory(category);
  const pool = imagesBySlug[slug] || imagesBySlug[DEFAULT_FOLDER];
  if (!pool || pool.length === 0) return null;
  return pool[hashString(businessId) % pool.length];
};
